import { useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

import {
  useGetMyFeedbackIssuesQuery,
  useAddFeedbackIssueCommentMutation,
  useWhoAmIQuery,
} from "../../services/api/__generated__/backend_gateway-types";
import { useApi } from "../../contexts/app/provider";
import { SysMessagesContext } from "../../contexts/sys_messages/context";
import { SysMessageLevel } from "../../contexts/sys_messages/type";

// Customer-facing status → chip palette key.
const STATUS_COLOR: Record<string, "info" | "warning" | "success" | "default"> =
  {
    Received: "info",
    "In progress": "warning",
    Fixed: "success",
    Closed: "default",
  };

// Linear priority (1-4) → label + chip palette. 0 (No priority) shows no chip.
const PRIORITY_CHIP: Record<
  number,
  { label: string; color: "error" | "warning" | "info" | "default" }
> = {
  1: { label: "Urgent", color: "error" },
  2: { label: "High", color: "warning" },
  3: { label: "Medium", color: "info" },
  4: { label: "Low", color: "default" },
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
}

/**
 * "See tickets" body: a read-only list of the feedback issues this customer has
 * filed, with status, click-to-copy ticket key, expand-to-read, and a per-ticket
 * comment composer. Customer-scoping is enforced server-side
 * (getMyFeedbackIssues only returns the viewed customer's tickets).
 */
export const FeedbackTicketsView = ({
  expanded,
}: {
  expanded: boolean;
}): JSX.Element => {
  const theme = useTheme();
  const { apiService } = useApi();
  const client = apiService.getClient();
  const { addSystemMessage } = useContext(SysMessagesContext);

  const { data: whoAmIData } = useWhoAmIQuery({ client });
  const myEmail = (whoAmIData?.whoAmI?.email ?? "").toLowerCase();

  // cache-and-network: a reopen paints instantly, then refreshes statuses.
  const { data, loading } = useGetMyFeedbackIssuesQuery({
    client,
    fetchPolicy: "cache-and-network",
  });
  const [addComment, { loading: commenting }] =
    useAddFeedbackIssueCommentMutation({ client });

  // null = untouched → default to the user's own tickets; "" = explicit Everyone.
  const [reporterChoice, setReporterChoice] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [commentFor, setCommentFor] = useState<string | null>(null);
  const [commentText, setCommentText] = useState("");
  const [commentFailed, setCommentFailed] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const result = data?.getMyFeedbackIssues;
  const issues = useMemo(() => result?.issues ?? [], [result]);

  const reporters = useMemo(
    () =>
      Array.from(
        new Set(
          issues.map((t) => t.reportedBy).filter((r): r is string => !!r)
        )
      ).sort(),
    [issues]
  );
  // Footer emails keep their original casing, so match case-insensitively.
  const myReporterEntry = useMemo(
    () => (myEmail ? reporters.find((r) => r.toLowerCase() === myEmail) ?? "" : ""),
    [reporters, myEmail]
  );
  // A background refresh can drop a previously-chosen reporter from the list,
  // leaving the Select on a value with no matching option (renders blank, list
  // sticks empty). Guard: any filter not in the current options falls back to
  // Everyone. ("" is always valid; myReporterEntry is derived from `reporters`.)
  const desiredFilter = reporterChoice ?? myReporterEntry;
  const reporterFilter =
    desiredFilter && !reporters.includes(desiredFilter) ? "" : desiredFilter;
  const visibleIssues = reporterFilter
    ? issues.filter((t) => t.reportedBy === reporterFilter)
    : issues;

  const handleCopyKey = async (id: string, identifier: string) => {
    try {
      await navigator.clipboard.writeText(identifier);
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
    } catch {
      // Clipboard unavailable — the key is visible to copy manually.
    }
  };

  const handleSendComment = async (issueId: string) => {
    const text = commentText.trim();
    if (!text || commenting) return;
    setCommentFailed(false);
    try {
      const res = await addComment({ variables: { issueId, comment: text } });
      if (res.data?.addFeedbackIssueComment?.success) {
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: "Comment added to the ticket.",
        });
        setCommentFor(null);
        setCommentText("");
      } else {
        setCommentFailed(true);
      }
    } catch {
      setCommentFailed(true);
    }
  };

  // First load (no cached data yet).
  if (loading && !result) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // Listing unavailable (creds/scope/Linear down) — soft fallback.
  if (result && !result.available) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Your tickets are temporarily unavailable. Please try again shortly, or
        reach out to the Atomic team directly.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Problems and requests your team has sent us, and where each one stands.
        Expand to read the full reports.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Typography variant="body2">Reported by</Typography>
        <TextField
          select
          size="small"
          value={reporterFilter}
          onChange={(e) => setReporterChoice(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Everyone</MenuItem>
          {reporters.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {visibleIssues.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No tickets yet.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: expanded ? "70vh" : "48vh",
            overflowY: "auto",
          }}
        >
          {visibleIssues.map((t) => {
            const isOpen = expandedId === t.id;
            return (
              <Box
                key={t.id}
                sx={{
                  py: 1.5,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={copiedId === t.id ? "Copied!" : t.identifier}
                    size="small"
                    icon={
                      copiedId === t.id ? undefined : (
                        <ContentCopyIcon sx={{ fontSize: 13 }} />
                      )
                    }
                    onClick={() => handleCopyKey(t.id, t.identifier)}
                    sx={{
                      fontFamily: "monospace",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  />
                  <Chip
                    label={t.status}
                    size="small"
                    color={STATUS_COLOR[t.status] ?? "default"}
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                  {PRIORITY_CHIP[t.priority] && (
                    <Chip
                      label={PRIORITY_CHIP[t.priority].label}
                      size="small"
                      color={PRIORITY_CHIP[t.priority].color}
                      variant="filled"
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    onClick={() => setExpandedId(isOpen ? null : t.id)}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      cursor: "pointer",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: isOpen ? "normal" : "nowrap",
                    }}
                  >
                    {t.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                  >
                    {formatDate(t.createdAt)}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Add a comment"
                    onClick={() => {
                      setCommentFor(commentFor === t.id ? null : t.id);
                      setCommentText("");
                      setCommentFailed(false);
                    }}
                  >
                    <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                {t.reportedBy && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Reported by {t.reportedBy} · {formatDate(t.createdAt)}
                  </Typography>
                )}

                {isOpen && t.description && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, whiteSpace: "pre-wrap", color: "text.primary" }}
                  >
                    {t.description}
                  </Typography>
                )}

                {commentFor === t.id && (
                  <Box sx={{ mt: 1.5 }}>
                    <TextField
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                      size="small"
                      placeholder="Add a comment for the Atomic team…"
                      autoFocus
                    />
                    {commentFailed && (
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.error.main, mt: 0.5, display: "block" }}
                      >
                        Couldn't add the comment. Please try again.
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setCommentFor(null);
                          setCommentText("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSendComment(t.id)}
                        disabled={commentText.trim().length === 0 || commenting}
                      >
                        {commenting ? "Adding…" : "Add comment"}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
